import { useEffect, useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { AUDIENCE_OPTIONS, DEPT_OPTIONS } from '../constants';
import type {
  Announcement,
  AttachmentUpload,
  CreateAnnouncementPayload,
  UpdateAnnouncementPayload,
} from '../types';

interface AnnouncementFormModalProps {
  open: boolean;
  announcement?: Announcement | null; // null = create
  loading?: boolean;
  onClose: () => void;
  onCreate: (payload: CreateAnnouncementPayload) => void;
  onUpdate: (id: string, payload: UpdateAnnouncementPayload) => void;
}

interface FormState {
  title: string;
  body: string;
  target_dept: string;
  target_audience: string;
}

const EMPTY: FormState = { title: '', body: '', target_dept: 'all', target_audience: 'all' };

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function AnnouncementFormModal({
  open,
  announcement,
  loading,
  onClose,
  onCreate,
  onUpdate,
}: AnnouncementFormModalProps) {
  const isEdit = !!announcement;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [attachment, setAttachment] = useState<AttachmentUpload | null>(null);
  const [attachmentName, setAttachmentName] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setAttachment(null);
    setAttachmentName('');
    if (announcement) {
      setForm({
        title: announcement.title ?? '',
        body: announcement.body ?? '',
        target_dept: announcement.target_dept ?? 'all',
        target_audience: announcement.target_audience ?? 'all',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, announcement]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      // backend rejects >20MB; guard client-side too
      setAttachment(null);
      setAttachmentName('');
      e.target.value = '';
      return;
    }
    const data = await readFileAsDataUrl(file);
    setAttachment({ data, name: file.name, type: file.type });
    setAttachmentName(file.name);
  };

  const clearAttachment = () => {
    setAttachment(null);
    setAttachmentName('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;

    if (isEdit && announcement) {
      // Attachments are not processed on PUT — update text/targeting only.
      onUpdate(announcement.id, {
        title: form.title.trim(),
        body: form.body.trim(),
        target_dept: form.target_dept,
        target_audience: form.target_audience,
      });
    } else {
      onCreate({
        title: form.title.trim(),
        body: form.body.trim(),
        target_dept: form.target_dept,
        target_audience: form.target_audience,
        attachment,
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Announcement' : 'New Announcement'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="announcement-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Publish Announcement'}
          </Button>
        </>
      }
    >
      <form id="announcement-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title *"
          name="ann-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Announcement title…"
          required
        />

        <div>
          <label htmlFor="ann-body" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Body *
          </label>
          <textarea
            id="ann-body"
            rows={5}
            value={form.body}
            onChange={(e) => set('body', e.target.value)}
            placeholder="Write your announcement…"
            required
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Target Department"
            name="ann-dept"
            value={form.target_dept}
            onChange={(e) => set('target_dept', e.target.value)}
            options={DEPT_OPTIONS}
          />
          <Select
            label="Target Audience"
            name="ann-audience"
            value={form.target_audience}
            onChange={(e) => set('target_audience', e.target.value)}
            options={AUDIENCE_OPTIONS}
          />
        </div>

        {/* Attachment — only on create (backend processes attachments on POST only) */}
        {!isEdit && (
          <div>
            <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Attachment (optional, ≤20MB)</label>
            {attachmentName ? (
              <div className="flex items-center justify-between rounded-DEFAULT border border-outline bg-surface-variant px-3 py-2">
                <span className="flex min-w-0 items-center gap-2 text-data-md text-on-surface">
                  <Paperclip className="h-4 w-4 shrink-0" />
                  <span className="truncate">{attachmentName}</span>
                </span>
                <button type="button" onClick={clearAttachment} aria-label="Remove attachment" className="rounded p-1 hover:bg-surface">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                <Paperclip className="h-4 w-4" /> Attach file
              </Button>
            )}
            <input ref={fileRef} type="file" className="hidden" onChange={handleFile} />
          </div>
        )}
      </form>
    </Modal>
  );
}
